import { cloneDeep, forEach, groupBy, map } from "lodash";
import { useGrowl } from "PFApp/use_growl";
import { Accordion } from "PFComponents/accordion/accordion";
import { Button } from "PFComponents/button/button";
import { Icon } from "PFComponents/icon";
import { LoadingDots } from "PFComponents/loading_dots";
import SidePanel from "PFComponents/side_panel/side_panel";
import InputFieldSet from "PFComponents/text/input_field_set";
import { Typography } from "PFComponents/typography";
import { useLocalisationKeysEdit } from "PFCore/hooks/queries/admin/localisation";
import { EntryType, Localisation, LocalisationEntry, LocalisationKeys } from "PFTypes";
import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

import css from "../localisation.module.scss";

type LocalisationKeysFormProps = {
  accountId: number;
  localisationKey: LocalisationKeys;
  localisation: Localisation;
};

const createFormMap = (localisation: LocalisationEntry[]) => groupBy(localisation, "locale");

export const LocalisationKeysForm = ({
  accountId,
  localisationKey,
  localisation
}: LocalisationKeysFormProps) => {
  const { t } = useTranslation("admin", { keyPrefix: "localisation.keysForm" });
  const { t: defaultT } = useTranslation("translation");
  const { t: localeT } = useTranslation("locales");
  const growl = useGrowl();

  const { mutate: submit, isLoading: isSaving } = useLocalisationKeysEdit(
    accountId,
    localisationKey,
    localisation.id
  );

  const [isFormVisible, setFormVisible] = useState(false);
  const [formData, setFormData] = useState(localisation.localisation);

  const handleFormError = useCallback(
    () => growl({ kind: "error", message: t("editLocalisationError") }),
    [growl, t]
  );
  const closeForm = useCallback(() => setFormVisible(false), []);
  const saveForm = useCallback(() => {
    submit(formData, {
      onError: handleFormError
    });
  }, [submit, formData, handleFormError]);

  const onLocaleChange = useCallback(
    (value: string, key: string, locale: string) => {
      const newFormData = cloneDeep(formData);

      const deepUpdate = (formData: LocalisationEntry[]) => {
        forEach(formData, (entry: LocalisationEntry) => {
          if (entry.type === EntryType.Collection) {
            deepUpdate(entry.value);
          }

          if (entry.key === key && entry.locale === locale) {
            entry.value = value;
          }
        });
      };

      deepUpdate(newFormData);

      setFormData(newFormData);
    },
    [setFormData, formData]
  );

  const renderMissingTranslationWarning = () => (
    <span className={css.missingTranslationWarning}>
      <Icon name="warning" size="sm" color="paletteOrange1" /> {t("missingTranslation")}
    </span>
  );

  const renderFieldInput = ({ key, value, locale }: LocalisationEntry) => (
    <InputFieldSet
      label={key}
      value={value || ""}
      kind="bordered"
      warning={!value && renderMissingTranslationWarning()}
      handleChange={(value) => onLocaleChange(value, key, locale)}
    />
  );

  const renderCollectionInput = ({ key, value }: LocalisationEntry, isTopLevelCollection) => {
    if (!isTopLevelCollection) {
      return renderInputs(value as LocalisationEntry[], isTopLevelCollection);
    }

    return (
      <div className={css.collectionFormBlock}>
        <Typography variant="bodyRegular">{key}</Typography>
        {renderInputs(value as LocalisationEntry[])}
      </div>
    );
  };

  const renderInputs = (localeFields: LocalisationEntry[], isTopLevelCollection?) =>
    map(localeFields, (entry: LocalisationEntry) =>
      entry.type !== EntryType.Collection
        ? renderFieldInput(entry)
        : renderCollectionInput(entry, isTopLevelCollection)
    );

  const renderForm = () =>
    map(createFormMap(formData), (localeFields: LocalisationEntry[], localeKey) => (
      <div className={css.formBlock}>
        <Accordion
          header={
            <Typography variant="bodyBold" noMargin>
              {localeT(localeKey as unknown as TemplateStringsArray)}
            </Typography>
          }
        >
          <div className={css.accordionBody}>{renderInputs(localeFields, true)}</div>
        </Accordion>
      </div>
    ));

  return (
    <>
      <Button small kind="secondary" onClick={() => setFormVisible(true)}>
        {defaultT("edit")}
      </Button>
      <SidePanel title={t(`title.${localisationKey}`)} show={isFormVisible} fullHeight onClose={closeForm}>
        <section className={css.form}>
          <div className={css.formBlock}>
            <Typography variant="labelRegular">{t("storedAs")}</Typography>
            <Typography variant="bodyBold" noMargin>
              {localisation.name}
            </Typography>
          </div>
          {renderForm()}
          <footer className={css.formFooter}>
            <Button text={defaultT("cancel")} kind="tertiary" onClick={closeForm} />
            <Button onClick={saveForm} disabled={isSaving}>
              {isSaving ? <LoadingDots /> : defaultT("save")}
            </Button>
          </footer>
        </section>
      </SidePanel>
    </>
  );
};
